import * as React from "react";
import { useCallback, useMemo } from "react";
import _ from "lodash";
import * as PropTypes from "prop-types";
import { format } from "lib/globalize-util";
import type { IScope } from "reducers/scope";
import Label from "components/form/label";
import Checkbox from "material-ui/Checkbox";
import { List, ListItem } from "material-ui/List";
import Dropdown from "components/form/dropdown";
import { ChangeWarehouseConfirmation } from "components/catalog-add-item/change-warehouse-confirmation";
import CheckBox from "material-ui/svg-icons/toggle/check-box";
import IndeterminateCheckBox from "material-ui/svg-icons/toggle/indeterminate-check-box";
import { $, merge } from "glamor";
import { DATATEST } from "constants/integration";
import { toDataTest } from "helpers/test";
import { makeStyles } from "@material-ui/styles";
import { Theme } from "@material-ui/core/styles";
import fp from "lodash/fp";
import { MODE } from "constants/restrictions";
import { themeOptions } from "styles/theme";

const { WHITELIST, BLACKLIST } = MODE;

export interface IOption<T = string> {
    label: string;
    value: T;
}

export interface IRestriction {
    field: string;
}

interface IProps {
    catalog: any;
    editable: boolean;
    scope: IScope;
    onSnackbarAdd: (message: { message: string }) => void;
    onSetIsSavingRestriction: (saving: boolean) => void;
    hideOptionsForConnectBrand?: boolean;
}

interface IState {
    options: {
        currencies: IOption[];
        groups: IOption[];
        subteams: IOption[];
        warehouses: IOption[];
    };
    currencies: IOption[];
    groups: IOption[];
    subteams: IOption[];
    warehouses: IOption[];
    confirmationOpen: boolean;
    fallbackWarehouses: IOption[] | null;
}

export const setOption = (field: string, value: string, label: string, mode: string = BLACKLIST) => ({
    field,
    value,
    label,
    mode,
});
export const getValues = (field, current) => current.filter((x) => x.field === field);
export const setValues = (options: IOption[], current: IRestriction[], field) =>
    _.intersectionBy(options, getValues(field, current), "value");
export const getPreviewLabel = (options: IOption[], values: IOption[], mode?: string) =>
    mode === WHITELIST
        ? _(options).intersectionBy(values, "value").map("label").join(", ")
        : _(options).differenceBy(values, "value").map("label").join(", ");

export class Restrictions extends React.PureComponent<IProps, IState> {
    static contextTypes = {
        router: PropTypes.object.isRequired,
    };

    private styles = {
        base: merge(
            {
                display: "block",
                width: "100%",
            },
            $(" .group-save", {
                textAlign: "left",
                fontSize: "13px",
                paddingTop: "5px",
                width: "100%",
                marginRight: "15px",
                marginBottom: "12px",
            }),
            $(" .button", {
                minWidth: "50px",
            }),
        ),
    };

    constructor(props: IProps) {
        super(props);

        const options = {
            currencies: _.map(props.scope.brand.settings.currencies, (value) =>
                setOption("currency_code", value, value),
            ),
            groups: _.map(props.scope.brand.modules.base.customer_groups, (group) =>
                setOption("customer_group", group._id, group.name),
            ),
            subteams: _.map(props.scope.brand.settings.subteams, ({ name }) => setOption("subteam", name, name)),
            warehouses: fp.compose(
                fp.map(({ name, _id }: { name: string; _id: string }) => setOption("warehouse", _id, name, WHITELIST)),
                fp.orderBy(["created_on"], ["desc"]),
            )(props.scope.brand.settings.warehouses),
        };

        this.state = {
            options,
            currencies: setValues(options.currencies, props.catalog.restrictions, "currency_code"),
            groups: setValues(options.groups, props.catalog.restrictions, "customer_group"),
            subteams: setValues(options.subteams, props.catalog.restrictions, "subteam"),
            warehouses: setValues(options.warehouses, props.catalog.restrictions, "warehouse"),
            confirmationOpen: false,
            fallbackWarehouses: null,
        };
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            this.props.scope.brand.hasPreference("set_warehouse_for_linesheets") &&
            prevState.warehouses !== this.state.warehouses &&
            _.size(prevState.warehouses) > _.size(this.state.warehouses)
        ) {
            this.setState({ confirmationOpen: true, fallbackWarehouses: prevState.warehouses });
        } else if (
            prevState.subteams !== this.state.subteams ||
            prevState.groups !== this.state.groups ||
            prevState.currencies !== this.state.currencies ||
            prevState.warehouses !== this.state.warehouses
        ) {
            this.handleSave();
        }
    }

    render() {
        const { catalog, editable, scope, hideOptionsForConnectBrand = false } = this.props;
        const { options, currencies, subteams, groups, warehouses } = this.state;
        const useCatalogWarehouses = !!scope.brand.getPreference("set_warehouse_for_linesheets");

        if (!editable) {
            return null;
        }

        return (
            <div {...this.styles.base} data-test={DATATEST.CATALOG.RESTRICTIONS_CONTAINER}>
                {useCatalogWarehouses && (
                    <Restriction
                        dataTest={DATATEST.CATALOG.VISIBLE_WAREHOUSES_SELECTOR}
                        label={format("Warehouse")}
                        placeholder={format("Select warehouse")}
                        // @ts-ignore
                        onChange={this.handleChange("warehouses")}
                        options={options.warehouses}
                        values={warehouses}
                        mode={WHITELIST}
                    />
                )}
                {catalog.type === "linesheet" && (
                    <>
                        <Restriction
                            dataTest={DATATEST.CATALOG.VISIBLE_CURRENCIES_SELECTOR}
                            label={format("Visible Currencies")}
                            placeholder={format("No visible currencies")}
                            // @ts-ignore
                            onChange={this.handleChange("currencies")}
                            options={options.currencies}
                            values={currencies}
                        />
                        {(hideOptionsForConnectBrand ? scope.brand.hasPreference("customer_groups") : true) && (
                            <Restriction
                                dataTest={DATATEST.CATALOG.ALLOWED_CUSTOMER_GROUPS_SELECTOR}
                                label={format("Allowed Customer Groups")}
                                placeholder={format("No customer groups")}
                                // @ts-ignore
                                onChange={this.handleChange("groups")}
                                options={options.groups}
                                values={groups}
                            />
                        )}
                        {!hideOptionsForConnectBrand && (
                            <Restriction
                                dataTest={DATATEST.CATALOG.ALLOWED_SUBTEAMS_SELECTOR}
                                label={format("Allowed Subteams")}
                                placeholder={format("No subteams")}
                                // @ts-ignore
                                onChange={this.handleChange("subteams")}
                                options={options.subteams}
                                values={subteams}
                            />
                        )}
                    </>
                )}
                <ChangeWarehouseConfirmation
                    isOpen={this.state.confirmationOpen}
                    onConfirm={this.handleSubmitConfirmation}
                    onClose={this.handleCloseConfirmation}
                />
            </div>
        );
    }

    // tslint:disable-next-line: member-ordering
    private handleChange = _.memoize(
        (field: keyof IState) => (values: IOption) => this.setState({ [field]: values } as any),
    );

    private handleCloseConfirmation = () => {
        this.setState({ confirmationOpen: false, warehouses: this.state.fallbackWarehouses, fallbackWarehouses: null });
    };
    private handleSubmitConfirmation = () => {
        this.setState({ confirmationOpen: false, fallbackWarehouses: null });
        this.handleSave();
    };

    private handleSave = _.debounce(() => {
        const { catalog, scope, onSnackbarAdd, onSetIsSavingRestriction } = this.props;
        onSetIsSavingRestriction(true);
        catalog.restrictions.bulkAdd(
            catalog.id,
            this.collectUpdates(catalog.type),
            (response) => {
                catalog.restrictions.reset();
                catalog.restrictions.add(response.restrictions);
                onSetIsSavingRestriction(false);
                if (!response.catalog) {
                    onSnackbarAdd({
                        message: format("The |linesheet| is no longer available", {
                            linesheet: scope.brand.hasPreference("linesheets_are_catalogs")
                                ? format("catalog")
                                : format("linesheet"),
                        }),
                    });
                    this.context.router.push(`/brand/${scope.brand._id}/linesheets`);
                }
            },
            (error) => {
                onSnackbarAdd({
                    message:
                        _.get(error, "body.message") ||
                        format("Oops! Something went wrong. Please refresh and try again."),
                });
                onSetIsSavingRestriction(false);
            },
        );
    }, 1000);

    private collectUpdates = (catalogType) => {
        if (catalogType === "custom-list") {
            return [...this.state.warehouses];
        }
        return [...this.state.currencies, ...this.state.groups, ...this.state.subteams, ...this.state.warehouses];
    };
}

const styles = {
    restrictionButton: {
        overflow: "hidden",
        width: "280px",
        borderRadius: "4px",
    },
    invalidButton: {
        border: `1px solid ${themeOptions.palette.error}`,
    },
    invalidLabel: {
        color: themeOptions.palette.error,
    },
    restrictionLabel: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
        width: "100%",
        textAlign: "left",
    },
    selectAll: {
        borderBottom: `1px solid ${themeOptions.palette.borderColor}`,
    },
    list: {
        minWidth: "200px",
    },
};

interface IRestrictionProps {
    label: string;
    options: IOption[];
    values: IOption[];
    onChange: (values: IOption[]) => void;
    placeholder?: string;
    dataTest?: string;
    isValid?: boolean;
    errorText?: string;
    mode?: string;
    isNewModalEnabled?: boolean;
}

export function Restriction(props: IRestrictionProps) {
    const {
        label,
        options,
        values,
        onChange,
        placeholder = format("No values"),
        dataTest,
        isValid = true,
        errorText,
        isNewModalEnabled = false,
    } = props;
    const { mode = BLACKLIST } = props;
    const classes = useStyles({ isValid });
    const allSelected = useMemo(
        () => (mode === BLACKLIST ? _.size(values) === 0 : _.size(values) === _.size(options)),
        [options, values, mode],
    );
    const valuesDict = useMemo(() => _.keyBy(values, "value"), [values]);

    const handleChange = useCallback(
        (idx) => (e, checked) => {
            const pick = mode === BLACKLIST ? _.reject : _.concat;
            const reject = mode === BLACKLIST ? _.concat : _.reject;
            const updated = checked ? pick(values, options[idx]) : reject(values, options[idx]);
            onChange(updated);
        },
        [onChange, values, options, mode],
    );

    const handleAll = useCallback(() => {
        const allCheckedValue = mode === BLACKLIST ? options : [];
        const allUncheckedValue = mode === BLACKLIST ? [] : options;
        onChange(allSelected ? allCheckedValue : allUncheckedValue);
    }, [onChange, mode, allSelected, options]);

    const previewLabel = getPreviewLabel(options, values, mode) || placeholder;
    const buttonStyle = isValid ? styles.restrictionButton : { ...styles.restrictionButton, ...styles.invalidButton };
    const labelStyle = isValid ? {} : { ...styles.invalidLabel };

    return (
        <div
            className={!isNewModalEnabled ? classes.restriction : ""}
            style={isNewModalEnabled ? { maxWidth: "100% !important" } : {}}
        >
            <Label style={labelStyle}>{label}</Label>
            <Dropdown
                data-test={dataTest || toDataTest(label)}
                label={previewLabel}
                buttonStyle={buttonStyle}
                labelStyle={styles.restrictionLabel}
                fullWidth={isNewModalEnabled}
            >
                <List style={styles.list}>
                    {_.size(options) > 1 && (
                        <ListItem
                            key="__all"
                            innerDivStyle={styles.selectAll}
                            primaryText={format("Select all")}
                            leftCheckbox={
                                <Checkbox
                                    onCheck={handleAll}
                                    checked={allSelected}
                                    checkedIcon={allSelected ? <CheckBox /> : <IndeterminateCheckBox />}
                                />
                            }
                        />
                    )}
                    {_.map(options, (option, idx) => {
                        const checked = mode === BLACKLIST ? !valuesDict[option.value] : !!valuesDict[option.value];
                        return (
                            <ListItem
                                key={option.value}
                                primaryText={option.label}
                                leftCheckbox={
                                    <Checkbox
                                        data-test={DATATEST.CATALOG.GROUP_ITEM_CHECKBOX}
                                        className={checked ? "checked" : ""}
                                        onCheck={handleChange(idx)}
                                        checked={checked}
                                    />
                                }
                            />
                        );
                    })}
                </List>
            </Dropdown>
            {!isValid && <div className={classes.errorText}>{errorText}</div>}
        </div>
    );
}

const useStyles = makeStyles<Theme, { isValid: boolean }>({
    restriction: {
        maxWidth: "300px",
        padding: "0 24px 8px 0",
        flexGrow: 0,
        flexShrink: 0,
    },
    errorText: {
        position: "relative",
        bottom: "2px",
        fontSize: "12px",
        lineHeight: "12px",
        color: themeOptions.palette.error,
        marginTop: "24px",
    },
});

export default Restrictions;
